import apiClient from './api';
import { AuthResponse, User, ApiError } from '../types/api';
import axios from 'axios';

// Constants for session management
const TOKEN_KEY = 'authToken';
const LAST_ACTIVITY_KEY = 'lastActivityTime';
const SESSION_REFRESH_INTERVAL = 6 * 24 * 60 * 60 * 1000; // 6 days in milliseconds

// Google client ID - should ideally be in an environment variable
const GOOGLE_CLIENT_ID = '797792678018-71st865l3ephv4edn6j6hng3qkpcl4le.apps.googleusercontent.com';

// redirect uri
const LINE_LOGIN_REDIRECT_URI = `${window.location.origin}/line-callback`;
const LINE_LOGIN_CHANNEL_ID = import.meta.env.VITE_LINE_LOGIN_CHANNEL_ID;
const LINE_LOGIN_CHANNEL_SECRET = import.meta.env.VITE_LINE_LOGIN_CHANNEL_SECRET;

// Helper function to update last activity time
const updateLastActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
};

export const authService = {
  /**
   * Register a new user with email
   * 
   * Note: If the email already exists and the password is correct,
   * it will automatically log in the existing user instead of returning an error.
   */
  register: async (email: string, password: string, name: string = '', phone: string = ''): Promise<AuthResponse> => {
    console.log('Register request:', { email, name, phone }); // Don't log password
    
    try {
      const response = await apiClient.post<AuthResponse>('/auth/register', {
        email,
        password,
        name,
        phone
      });
      
      console.log('Register response:', response.data);
      
      // Store token if it exists
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        updateLastActivity();
        initSessionRefresh();
      }
      
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Register error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Register or start verification with phone number
   */
  registerWithPhone: async (phone: string, preferredProvider?: 'smsmkt' | 'thaibulksms'): Promise<{ success: boolean, message: string, existingUser?: boolean }> => {
    try {
      const response = await apiClient.post<{ success: boolean, message: string, existingUser?: boolean }>('/auth/register-phone', {
        phone,
        preferredProvider
      });
      
      // Even if status code is 200, check if success is false and throw an error
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Failed to send verification code');
      }
      
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Phone registration error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  
  /**
   * Get current user data
   */
  getCurrentUser: async (): Promise<User> => {
    try {
      const response = await apiClient.get<{ user: User, token?: string }>('/auth/me');
      
      // Update token if a new one is provided
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        updateLastActivity();
      }
      
      return response.data.user;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Get current user error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Send email verification
   */
  sendVerificationEmail: async (email: string): Promise<{ success: boolean }> => {
    try {
      const response = await apiClient.post<{ success: boolean }>('/auth/send-verification', {
        email
      });
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Send verification email error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Request password reset email
   */
  requestPasswordReset: async (email: string): Promise<{ success: boolean, message: string }> => {
    try {
      const response = await apiClient.post<{ success: boolean, message: string }>('/auth/request-password-reset', {
        email
      });
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Password reset request error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Reset password with token
   */
  resetPassword: async (token: string, password: string): Promise<{ success: boolean, message: string }> => {
    try {
      const response = await apiClient.post<{ success: boolean, message: string }>('/auth/reset-password', {
        token,
        password
      });
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Password reset error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Login a user
   */
  login: async (email: string, password: string): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/login', {
        email,
        password
      });
      
      // Store the token in localStorage
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        updateLastActivity();
        initSessionRefresh();
      }
      
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Login error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Login with Google
   */
  loginWithGoogle: async (idToken: string): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/google-auth', {
        token: idToken
      });
      
      // Store the token in localStorage
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        updateLastActivity();
        initSessionRefresh();
      }
      
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Google login error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Register with Google - auto verifies email
   */
  registerWithGoogle: async (idToken: string): Promise<AuthResponse> => {
    try {
      // Use the same Google auth endpoint but with an additional parameter
      const response = await apiClient.post<AuthResponse>('/auth/google-auth', {
        token: idToken,
        register: true  // This indicates to backend that it should create account if needed
      });
      
      // Store the token in localStorage
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        updateLastActivity();
        initSessionRefresh();
      }
      
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Google registration error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Initialize Google OAuth
   */
  initGoogleAuth: async (): Promise<void> => {
    try {
      // Check if already loaded
      if (window.google && window.google.accounts) {
        return;
      }
      
      // Create a static flag to track loading attempts
      if ((authService.initGoogleAuth as any).isLoading) {
        // Wait for the current loading attempt to finish
        await new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            if (window.google && window.google.accounts) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
          
          // Set a max timeout in case the loading never completes
          setTimeout(() => {
            clearInterval(checkInterval);
            resolve();
          }, 5000);
        });
        return;
      }
      
      // Mark as loading
      (authService.initGoogleAuth as any).isLoading = true;
      
      try {
        // Load the Google Identity Services library
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://accounts.google.com/gsi/client';
          script.async = true;
          script.defer = true;
          script.onload = () => resolve();
          script.onerror = (error) => {
            console.error('Failed to load Google Identity Services:', error);
            reject(new Error('Failed to load Google Identity Services. It might be blocked by an ad blocker or privacy extension.'));
          };
          
          // Set timeout to detect blocking
          const timeout = setTimeout(() => {
            reject(new Error('Google Identity Services loading timed out. It might be blocked by an ad blocker or privacy extension.'));
          }, 5000);
          
          script.addEventListener('load', () => {
            clearTimeout(timeout);
            resolve();
          });
          
          document.head.appendChild(script);
        });
        
        // Verify initialization
        if (!window.google || !window.google.accounts) {
          throw new Error('Google Sign-In could not be initialized. It may be blocked by browser settings or extensions.');
        }
      } finally {
        // Reset loading flag
        (authService.initGoogleAuth as any).isLoading = false;
      }
    } catch (error) {
      console.error('Error initializing Google Auth:', error);
      throw error;
    }
  },
  
  /**
   * Get Google OAuth token
   */
  getGoogleToken: async (): Promise<string> => {
    try {
      await authService.initGoogleAuth();
      
      return new Promise<string>((resolve, reject) => {
        try {
          if (!window.google || !window.google.accounts || !window.google.accounts.oauth2) {
            reject(new Error('Google OAuth API not available. It might be blocked by your browser.'));
            return;
          }
          
          const client = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: 'email profile',
            callback: (tokenResponse: { error?: string; access_token?: string }) => {
              if (tokenResponse.error) {
                reject(new Error(tokenResponse.error));
                return;
              }
              if (!tokenResponse.access_token) {
                reject(new Error('No access token received from Google'));
                return;
              }
              resolve(tokenResponse.access_token);
            },
          });
          
          // Set a timeout to detect if the OAuth flow doesn't start
          const timeout = setTimeout(() => {
            reject(new Error('Google OAuth flow timed out. It might be blocked by your browser settings.'));
          }, 5000);
          
          try {
            client.requestAccessToken();
            clearTimeout(timeout);
          } catch (requestError) {
            clearTimeout(timeout);
            console.error('Error requesting Google access token:', requestError);
            reject(new Error('Failed to start Google OAuth flow. It might be blocked by your browser.'));
          }
        } catch (error) {
          console.error('Google OAuth initialization error:', error);
          reject(error instanceof Error ? error : new Error('Unknown error initializing Google OAuth'));
        }
      });
    } catch (error) {
      console.error('Failed to get Google token:', error);
      throw error instanceof Error ? error : new Error('Unknown error getting Google token');
    }
  },
  
  /**
   * Login with phone number
   */
  loginWithPhone: async (phone: string, password: string): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/login-phone', {
        phone,
        password
      });
      
      // Store the token in localStorage
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        updateLastActivity();
        initSessionRefresh();
      }
      
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Phone login error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Request verification code for phone login
   */
  requestLoginCode: async (phone: string, preferredProvider?: 'smsmkt' | 'thaibulksms'): Promise<{ success: boolean, message: string }> => {
    try {
      const response = await apiClient.post<{ success: boolean, message: string }>('/auth/request-login-code', {
        phone,
        preferredProvider
      });
      
      // Even if status code is 200, check if success is false and throw an error
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Failed to send verification code');
      }
      
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Login code request error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Verify phone login with code
   */
  verifyLoginCode: async (phone: string, code: string): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/verify', {
        phone,
        code,
        type: 'login'
      });
      
      // Store the token in localStorage
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        updateLastActivity();
        initSessionRefresh();
      }
      
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Login code verification error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Logout the current user
   */
  logout: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  },
  
  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem(TOKEN_KEY);
  },
  
  /**
   * Get the current user's token
   */
  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },
  
  /**
   * Refresh the session manually
   */
  refreshSession: async (): Promise<boolean> => {
    try {
      if (authService.isAuthenticated()) {
        await authService.getCurrentUser();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Manual session refresh failed:', error);
      return false;
    }
  },
  
  /**
   * Get Google OAuth URL for redirect or popup-based authentication
   * This provides a fallback when the Google Sign-In API can't be loaded
   */
  getGoogleOAuthUrl: async (returnTo: string = '/flowers', isRegistration: boolean = false): Promise<string> => {
    try {
      // Google OAuth 2.0 parameters
      // Use the React component route for handling the callback
      const redirectUri = `${window.location.origin}/google-callback`;
      
      // Create the OAuth 2.0 URL
      const baseUrl = 'https://accounts.google.com/o/oauth2/v2/auth';
      const params = new URLSearchParams({
        client_id: GOOGLE_CLIENT_ID,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'email profile',
        access_type: 'online',
        prompt: 'select_account',
        state: encodeURIComponent(JSON.stringify({ returnTo, isRegistration }))
      });
      
      return `${baseUrl}?${params.toString()}`;
    } catch (error) {
      console.error('Error creating Google OAuth URL:', error);
      throw error instanceof Error ? error : new Error('Failed to create Google OAuth URL');
    }
  },
  
  /**
   * Exchange authorization code for token
   * Used by the frontend OAuth callback page
   */
  exchangeGoogleAuthCode: async (code: string, redirectUri: string): Promise<AuthResponse> => {
    try {
      // Call the GET endpoint of our consolidated google-auth API
      const url = `/api/auth/google-auth?code=${encodeURIComponent(code)}`;
      const response = await apiClient.get<AuthResponse>(url);
      
      // Store the token in localStorage
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        updateLastActivity();
        initSessionRefresh();
      }
      
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Google code exchange error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Connect user account with LINE
   * This redirects to the LINE login page and will be handled by a callback
   */
  connectLine: async (): Promise<void> => {
    try {
      // Create the authorization URL with required parameters
      const clientId = LINE_LOGIN_CHANNEL_ID; // Your LINE Channel ID
      const redirectUri = LINE_LOGIN_REDIRECT_URI; // Your callback URL
      const state = Math.random().toString(36).substring(2, 15);; // Use a random state for security
      
      // Build the authorization URL
      const authUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&scope=profile%20openid%20email`;
      
      // // Store state in localStorage to verify when returning
      localStorage.setItem('line_auth_state', state);
      // // Redirect user to LINE login
      window.location.href = authUrl;
    } catch (error) {
      console.error('LINE connect error:', error);
      throw error;
    }
  },
  
  /**
   * Disconnect LINE from user account
   */
  disconnectLine: async (): Promise<{ success: boolean, message: string }> => {
    try {
      const response = await apiClient.post<{ success: boolean, message: string }>('/auth/disconnect-line');
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('LINE disconnect error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
  
  /**
   * Login with LINE
   * Used by the callback after LINE authentication
   */
  handleLineCallback: async (code: string, state: string): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/register-line', {
        code,
        state
      });
      
      // Store token if it exists
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        updateLastActivity();
        initSessionRefresh();
      }
      
      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('LINE callback error:', apiError.response?.data || apiError.message);
      throw error;
    }
  },
};

// Initialize session refresh mechanism
const initSessionRefresh = () => {
  // Only set up refresh if user is authenticated
  if (authService.isAuthenticated()) {
    // Set up periodic session refresh
    const refreshInterval = setInterval(async () => {
      try {
        // Only refresh if user is still authenticated and NOT on the login page
        if (authService.isAuthenticated() && 
            typeof window !== 'undefined' && 
            !window.location.pathname.includes('/login')) {
          
          const userData = await authService.getCurrentUser();
          console.log('Session refreshed successfully');
        } else {
          clearInterval(refreshInterval);
        }
      } catch (error) {
        console.error('Session refresh failed:', error);
      }
    }, SESSION_REFRESH_INTERVAL);
    
    // Clean up interval on page unload
    window.addEventListener('beforeunload', () => {
      clearInterval(refreshInterval);
    });
  }
};

// Initialize session refresh on module load if user is authenticated
if (typeof window !== 'undefined') {
  // Only run in browser environment
  setTimeout(() => {
    if (authService.isAuthenticated()) {
      initSessionRefresh();
    }
  }, 0);
}

export default authService; 